// src/components/vendor/pickup/PickupForm.tsx

import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import ItemSelection from '../ItemSelection';
import PickupTypeSelection from '../PickupTypeSelection';
import QuickPickup from '../QuickPickup';
import type { Pickup, PickupItem, PickupRequestItem, Product, ProductVariant, Store } from '../../../types';
import pickupsAPI from '../../../api/pickup';
import ModalShell from '../../common/ModalShell';
import PickupAddressSelection from './PickupAddressSelection';
import AddressForm from '../../address/AddressForm';
import { vendorAPI } from '../../../api/vendor';
import { formatDateEnglish } from '../../../utils/date';
import { parseISO } from 'date-fns';
import toast from '../../../utils/toast';
import addressAPI from '../../../api/addresses';
import PickupLabelsStep from './PickupLabelsStep';
import { buildPickupItems } from '../../../utils/pickup';
import ConfirmationModal from '../../common/ConfirmationModal';
import PickupDetailedStep from './DetailedPickup';

interface PickupFormProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => Promise<void>;
    isProcessing?: boolean;
    resumePickup?: Pickup | null;
}

// type SelectedItem = {
//     product: Product;
//     variant?: ProductVariant;
//     quantity: number;
//     _pickup_quantity: number;
//     _variant_quantities: any;
// };

type Step = 'items' | 'address' | 'type' | 'detailed' | 'labels';

const PickupForm: React.FC<PickupFormProps> = ({
    isOpen,
    onClose,
    onSubmit,
    isProcessing = false,
    resumePickup,
}) => {
    const [step, setStep] = useState<Step>('items');
    const [selectedProducts, setSelectedProducts] = useState<PickupItem[]>([]);
    const [selectedAddress, setSelectedAddress] = useState<any>(null);
    const [storeAddresses, setStoreAddresses] = useState<any[]>([]);
    const [loadingAddresses, setLoadingAddresses] = useState(false);
    const [showAddressForm, setShowAddressForm] = useState(false);
    const [addressFormStoreId, setAddressFormStoreId] = useState<number | null>(null);
    const [invalidProductIds, setInvalidProductIds] = useState<number[]>([]);
    const [boxCount, setBoxCount] = useState(1);
    const [selectedType, setSelectedType] = useState<'quick' | 'detailed' | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [generatedBoxes, setGeneratedBoxes] = useState<any[]>([]);
    const [mergedPdfUrl, setMergedPdfUrl] = useState<string | null>(null);
    const [pickupPhotos, setPickupPhotos] = useState<File[]>([]);
    const [noPrinter, setNoPrinter] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [availableFrom, setAvailableFrom] = useState('09:00');
    const [cities, setCities] = useState<any[]>([]);
    const [isCreating, setIsCreating] = useState(false);
    const [createStatus, setCreateStatus] = useState<'idle' | 'creating_request' | 'generating_labels' | 'done' | 'error'>('idle');
    const [createdPickupId, setCreatedPickupId] = useState<number | null>(null);
    const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
    const [showBackWarning, setShowBackWarning] = useState(false);
    const [assignments, setAssignments] = useState<Record<number, Record<number, number>>>({});
    const [detailedItems, setDetailedItems] = useState<PickupRequestItem[]>([]);
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [showDetailedConfirm, setShowDetailedConfirm] = useState(false);
    const getDefaultPickupDate = () => {
        const d = new Date();
        d.setDate(d.getDate() + 1);
        return d.toISOString().split('T')[0];
    };
    const [pickupDate, setPickupDate] = useState(getDefaultPickupDate());

    useEffect(() => {
        if (!isOpen) return;
        addressAPI.getCities()
            .then(res => setCities(res.data || res))
            .catch(err => console.error('Failed to fetch cities:', err));
    }, [isOpen]);

    const selectedStores = useMemo(() => {
        const map = new Map<number, Store>();
        selectedProducts.forEach(i => {
            const s = i.store_detail;
            if (s && !map.has(s.id)) map.set(s.id, s);
        });
        return Array.from(map.values());
    }, [selectedProducts]);

    const selectedStoreIds = useMemo(
        () => {
            return selectedStores.map(s => s.id)
        },
        [selectedStores]
    );

    useEffect(() => {
        if (selectedStoreIds.length === 0) {
            setStoreAddresses([]);
            return;
        }
        setLoadingAddresses(true);
        pickupsAPI.getAddressesForStores(selectedStoreIds)
            .then(setStoreAddresses)
            .catch(err => console.error('Failed to fetch addresses:', err))
            .finally(() => setLoadingAddresses(false));

    }, [selectedStoreIds.join(',')]);

    useEffect(() => {
        if (!resumePickup) return;
        const state = resumePickup.draft_state;
        if (!state) return;

        setSelectedProducts(state.selectedProducts ?? []);
        setSelectedAddress(state.selectedAddress ?? null);
        setPickupDate(resumePickup.pickup_date ?? '');
        setAvailableFrom(resumePickup.available_from ?? '');
        setSelectedType(resumePickup.pickup_type ?? null);
        setBoxCount(resumePickup.boxes?.length || 1);
        setCreatedPickupId(resumePickup.id);
        setDetailedItems(resumePickup.items || []);
        setGeneratedBoxes(resumePickup.boxes ?? []);
        setExistingPhotos((resumePickup.photos ?? []).map(p => p.image));

        // decide starting step
        const labelsGenerated = (resumePickup.boxes ?? []).some(b => b.label_generated_at);

        if (labelsGenerated) {
            // already past assignment and labels
            setStep('labels');
        } else if (resumePickup.pickup_type === 'detailed' && (resumePickup.boxes?.length ?? 0) > 1) {
            // detailed, assignments not yet made
            setStep('detailed');
        } else {
            // quick, or detailed with 1 box (shouldn't happen but safe)
            setStep('type');
        }
    }, [resumePickup]);

    const itemsValid = useMemo(
        () => detailedItems.every(i => {
            const total = Object.values(assignments[i.id] || {}).reduce((a, b) => a + b, 0);
            return total === i.quantity;
        }),
        [detailedItems, assignments]
    );

    const boxesValid = useMemo(
        () => generatedBoxes.every(b => {
            const total = Object.values(assignments).reduce(
                (sum, row) => sum + (row[b.id] || 0),
                0
            );
            return total > 0;
        }),
        [generatedBoxes, assignments]
    );

    const allValid = useMemo(() => {
        const itemsOk = detailedItems.every(i => {
            const total = Object.values(assignments[i.id] || {}).reduce((a, b) => a + b, 0);
            return total === i.quantity;
        });
        const boxesOk = generatedBoxes.every(b => {
            const total = Object.values(assignments).reduce(
                (sum, row) => sum + (row[b.id] || 0),
                0
            );
            return total > 0;
        });
        return itemsOk && boxesOk;
    }, [detailedItems, generatedBoxes, assignments]);

    // const handleDetailedContinue = async () => {
    //     setAttemptedSubmit(true);

    //     if (!itemsValid) {
    //         toast.error('Some items are not fully assigned');
    //         return;
    //     }
    //     if (!boxesValid) {
    //         toast.error('Some boxes are empty');
    //         return;
    //     }

    //     setIsCreating(true);
    //     setCreateStatus('generating_labels');
    //     try {
    //         const assignmentsPayload = Object.entries(assignments).flatMap(
    //             ([itemId, boxMap]) =>
    //                 Object.entries(boxMap)
    //                     .filter(([, qty]) => qty > 0)
    //                     .map(([boxId, qty]) => ({
    //                         item_id: Number(itemId),
    //                         box_id: Number(boxId),
    //                         quantity: qty,
    //                     }))
    //         );

    //         const labels = await pickupsAPI.generateLabels(createdPickupId!, {
    //             assignments: assignmentsPayload,
    //         });
    //         setGeneratedBoxes(labels.boxes);
    //         setMergedPdfUrl(labels.merged_pdf_url);
    //         setStep('labels');
    //     } catch (err) {
    //         console.error('Failed to assign boxes:', err);
    //         toast.error('Failed to assign boxes');
    //     } finally {
    //         setIsCreating(false);
    //     }
    // };

    const handleDetailedContinue = () => {
        // validate
        if (!allValid) {
            setAttemptedSubmit(true);
            if (!itemsValid) toast.error('Some items are not fully assigned');
            else if (!boxesValid) toast.error('Some boxes are empty');
            return;
        }
        console.log("ASDGFGAFDS")
        setShowDetailedConfirm(true);
    };

    const confirmDetailedContinue = async () => {
        setShowDetailedConfirm(false);
        setIsCreating(true);
        setCreateStatus('generating_labels');
        try {
            const assignmentsPayload = Object.entries(assignments).flatMap(
                ([itemId, boxMap]) =>
                    Object.entries(boxMap)
                        .filter(([, qty]) => qty > 0)
                        .map(([boxId, qty]) => ({
                            item_id: Number(itemId),
                            box_id: Number(boxId),
                            quantity: qty,
                        }))
            );
            const labels = await pickupsAPI.generateLabels(createdPickupId!, { assignments: assignmentsPayload });
            setGeneratedBoxes(labels.boxes);
            setMergedPdfUrl(labels.merged_pdf_url);
            setStep('labels');
        } catch (err) {
            toast.error('Failed to generate labels');
        } finally {
            setIsCreating(false);
        }
    };

    const handleTypeContinue = async () => {
        if (!selectedType || boxCount < 1) return;
        setIsCreating(true);
        setCreateStatus('creating_request');
        setAssignments({});
        setAttemptedSubmit(false);

        const payload = {
            address_id: selectedAddress?.address,
            stores: selectedStoreIds,
            pickup_date: pickupDate,
            available_from: availableFrom,
            pickup_type: selectedType,
            box_count: boxCount,
            items: buildPickupItems(selectedProducts),
            draft_state: {
                selectedProducts,
                selectedAddress,
                selectedStoreIds,
            },
        };

        try {
            let pickupId = createdPickupId;

            if (createdPickupId) {
                const res = await pickupsAPI.updatePickup(createdPickupId, payload);
                setGeneratedBoxes(res.boxes || []);
                setDetailedItems(res.items || []);
            } else {
                const pickup = await pickupsAPI.createPickup(payload);
                pickupId = pickup.id;
                setCreatedPickupId(pickup.id);
                setGeneratedBoxes(pickup.boxes || []);
                setDetailedItems(pickup.items || []);
            }
            if (selectedType === 'quick') {
                setCreateStatus('generating_labels');
                const labels = await pickupsAPI.generateLabels(pickupId!);
                setGeneratedBoxes(labels.boxes);
                setMergedPdfUrl(labels.merged_pdf_url);
                setStep('labels');
            } else {
                // detailed — go to assignment step
                setStep('detailed');
            }
        } catch (err) {
            console.error('Failed to save pickup:', err);
            setCreateStatus('error');
            toast.error('Failed to create pickup request');
        } finally {
            setIsCreating(false);
        }
    };

    const handleAddressFormSubmit = async (formData: any) => {
        try {
            const saved = await vendorAPI.addVendorAddress({
                ...formData,
                store: addressFormStoreId,
            });
            const fresh = await pickupsAPI.getAddressesForStores(selectedStoreIds);
            setStoreAddresses(fresh);
            const newLink = fresh.find((sa: any) => sa.id === saved.id);
            if (newLink) setSelectedAddress(newLink);
            setShowAddressForm(false);
            setAddressFormStoreId(null);
        } catch (err) {
            console.error('Failed to add pickup address:', err);
        }
    };

    const handleBack = () => {
        if (step === 'labels' && createdPickupId && generatedBoxes.some(b => b.label_generated_at)) {
            setShowBackWarning(true);
            return;
        }
        if (step === 'address') setStep('items');
        else if (step === 'type') setStep('address');
        else if (step === 'detailed') setStep('type');
        else if (step === 'labels') {
            if (selectedType === 'detailed') setStep('detailed');
            else setStep('type');
        }
    };

    const confirmBack = () => {
        setShowBackWarning(false);
        if (selectedType === 'detailed') setStep('detailed');
        else setStep('type');
    };

    const handleNext = () => {
        if (selectedProducts.length === 0) {
            toast.error('Select at least one product');
            return;
        }
        if (!pickupDate) {
            toast.error('Choose a pickup date');
            return;
        }

        const invalid: number[] = [];
        selectedProducts.forEach(p => {
            const isMulti = p.variants && p.variants.length > 1;
            if (isMulti) {
                const total = Object.values(p._variant_quantities ?? {}).reduce(
                    (a: number, b: any) => a + (b as number), 0
                );
                if (total === 0) invalid.push(p.id);
            } else {
                if (!p._pickup_quantity || p._pickup_quantity < 1) invalid.push(p.id);
            }
        });

        if (invalid.length > 0) {
            setInvalidProductIds(invalid);
            toast.error('Some products have no quantity set');
            return;
        }

        setInvalidProductIds([]);
        setStep('address');
    };

    const handleProductsChange = (products: any[]) => {
        setSelectedProducts(products);
        if (invalidProductIds.length > 0) setInvalidProductIds([]);
    };

    const handleFinalSubmit = async () => {
        if (pickupPhotos.length === 0 && existingPhotos.length === 0) {
            toast.error('Upload at least one photo');
            return;
        }
        setIsSubmitting(true);
        try {
            if (pickupPhotos.length > 0) {
                const fd = new FormData();
                pickupPhotos.forEach(f => fd.append('photos', f));
                await pickupsAPI.uploadPhotos(createdPickupId!, fd);
            }
            await pickupsAPI.submitPickup(createdPickupId!);
            toast.success('Pickup request submitted');
            onClose();
        } catch (err) {
            toast.error('Failed to submit: ' + err);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    // Step 1: Select Items
    if (step === 'items') {
        return (
            <ModalShell
                isOpen={isOpen}
                onClose={onClose}
                title="New Pickup Request"
                subtitle="Select products to send to warehouse"
                isProcessing={isProcessing}
                footer={
                    <div className="flex justify-between">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isProcessing}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleNext}
                            disabled={selectedProducts.length === 0 || !pickupDate || isProcessing}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Next
                        </button>
                    </div>
                }
            >
                <div className="flex flex-col h-full">
                    <div className="flex-1">
                        <ItemSelection
                            selectedProducts={selectedProducts}
                            onChange={handleProductsChange}
                            invalidProductIds={invalidProductIds}
                        />
                    </div>

                    {/* Date / time fields below the product selection */}
                    <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Pickup Date <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                value={pickupDate}
                                onChange={(e) => setPickupDate(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />

                            {pickupDate && (
                                <p className="text-xs text-gray-500 mt-1">
                                    {formatDateEnglish(String(parseISO(pickupDate)))}
                                </p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Available From
                            </label>
                            <input
                                type="time"
                                value={availableFrom}
                                onChange={(e) => setAvailableFrom(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    </div>
                </div>
            </ModalShell>
        );
    }

    // Step 2: Select Address
    if (step === 'address') {
        return (
            <>
                <ModalShell
                    isOpen={isOpen}
                    onClose={onClose}
                    title="Pickup Address"
                    subtitle="Choose where the courier should collect from"
                    isProcessing={isProcessing}
                    footer={
                        <div className="flex justify-between">
                            <button
                                type="button"
                                onClick={handleBack}
                                disabled={isProcessing}
                                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                onClick={() => setStep('type')}
                                disabled={!selectedAddress || isProcessing}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Next
                            </button>
                        </div>
                    }
                >
                    <PickupAddressSelection
                        storeAddresses={storeAddresses}
                        selectedStores={selectedStores}
                        selectedAddressId={selectedAddress?.id ?? null}
                        onSelect={(id) => {
                            const link = storeAddresses.find(sa => sa.id === id);
                            setSelectedAddress(link);
                        }}
                        onAddAddress={(storeId) => {
                            setAddressFormStoreId(storeId);
                            setShowAddressForm(true);
                        }}
                        error={errors.address}
                        loading={loadingAddresses}
                    // notes={notes}
                    // onNotesChange={setNotes}
                    />
                </ModalShell>

                {showAddressForm && (
                    <AddressForm
                        onCancel={() => {
                            setShowAddressForm(false);
                            setAddressFormStoreId(null);
                        }}
                        onSubmit={handleAddressFormSubmit}
                        mode="vendor"
                        defaultStoreId={addressFormStoreId}
                        storeOptions={selectedStores}
                        showEmailField={false}
                        cities={cities}
                    />
                )}
            </>
        );
    }
    const isDetailedSingleBox = selectedType === 'detailed' && boxCount === 1;
    // Step 3a: Choose Pickup Type
    if (step === 'type') {
        return (
            <ModalShell
                isOpen={isOpen}
                onClose={onClose}
                title="Choose Pickup Type"
                subtitle="Select how you want to send your items"
                isProcessing={isProcessing}
                footer={
                    <div className="flex justify-between">
                        <button
                            type="button"
                            onClick={handleBack}
                            disabled={isProcessing}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                if (isDetailedSingleBox) {
                                    toast.error('Detailed pickup needs at least 2 boxes. Increase the box count or use Quick pickup.');
                                    return;
                                }
                                handleTypeContinue();
                            }}
                            disabled={!selectedType || boxCount < 1 || isCreating}
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${isDetailedSingleBox || !selectedType || boxCount < 1
                                ? 'bg-gray-300 text-white cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                } disabled:opacity-50`}
                        >
                            {selectedType === 'detailed' ? 'Assign Items to Boxes' : 'Continue to Labels'}
                        </button>
                    </div>
                }
            >
                {isCreating && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40">
                        <div className="bg-white rounded-xl p-6 flex items-center gap-3 shadow-xl">
                            <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                            <span className="text-sm text-gray-700">
                                {createStatus === 'creating_request' && 'Creating pickup request...'}
                                {createStatus === 'generating_labels' && 'Generating labels...'}
                                {createStatus === 'done' && 'Done'}
                            </span>
                        </div>
                    </div>
                )}
                <PickupTypeSelection
                    selectedType={selectedType}
                    onSelectType={setSelectedType}
                    boxCount={boxCount}
                    onBoxCountChange={setBoxCount}
                />
                <ConfirmationModal
                    isOpen={showDetailedConfirm}
                    onClose={() => setShowDetailedConfirm(false)}
                    onConfirm={confirmDetailedContinue}
                    title="Ready to generate labels?"
                    message={
                        <div className="space-y-3 text-sm text-gray-700">
                            <p>
                                Once labels are generated, box codes are locked and cannot be changed
                                without regenerating.
                            </p>
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                                <p className="font-medium">Mark your boxes now</p>
                                <p className="mt-1">
                                    Physically mark each box as Box 1, Box 2, etc. matching the assignments
                                    you just made. You'll need to attach the correct label to the correct box.
                                </p>
                            </div>
                        </div>
                    }
                    confirmText="Yes, Generate Labels"
                    cancelText="Go Back"
                    type="warning"
                    isProcessing={isCreating}
                />
            </ModalShell>
        );
    }

    // Step 3b: Assign items to packages
    if (step === 'detailed') {
        return (
            <ModalShell
                isOpen={isOpen}
                onClose={onClose}
                title="Assign Items to Boxes"
                subtitle="Specify what goes in each box"
                isProcessing={isCreating}
                footer={
                    <div className="flex justify-between">
                        <button
                            type="button"
                            onClick={handleBack}
                            disabled={isCreating}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            onClick={handleDetailedContinue}
                            disabled={isCreating}
                            className={`px-6 py-2 rounded-lg text-sm font-medium transition-colors ${!allValid
                                ? 'bg-gray-300 text-white cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                                } disabled:opacity-50`}
                        >
                            {isCreating ? 'Generating...' : 'Generate Labels'}
                        </button>
                    </div>
                }
            >
                <PickupDetailedStep
                    items={detailedItems}
                    boxes={generatedBoxes}
                    assignments={assignments}
                    onAssignmentsChange={(a) => {
                        setAssignments(a);
                        if (attemptedSubmit) setAttemptedSubmit(false);
                    }}
                    attemptedSubmit={attemptedSubmit}
                />

                <ConfirmationModal
                    isOpen={showDetailedConfirm}
                    onClose={() => setShowDetailedConfirm(false)}
                    onConfirm={confirmDetailedContinue}
                    title="Ready to generate labels?"
                    message={
                        <div className="space-y-3 text-sm text-gray-700">
                            <p>
                                Once labels are generated, box codes are locked and cannot be changed
                                without regenerating.
                            </p>
                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
                                <p className="font-medium">Mark your boxes now</p>
                                <p className="mt-1">
                                    Physically mark each box as Box 1, Box 2, etc. matching the assignments
                                    you just made. You'll need to attach the correct label to the correct box.
                                </p>
                            </div>
                        </div>
                    }
                    confirmText="Yes, Generate Labels"
                    cancelText="Go Back"
                    type="warning"
                    isProcessing={isCreating}
                />
            </ModalShell>
        );
    }

    // Step 4: Label and upload photo proof
    if (step === 'labels') {
        return (
            <ModalShell
                isOpen={isOpen}
                onClose={onClose}
                title="Labels & Photos"
                subtitle="Download labels, apply to boxes, then upload photos"
                isProcessing={isSubmitting}
                footer={
                    <div className="flex justify-between">
                        <button
                            type="button"
                            onClick={handleBack}
                            disabled={isSubmitting}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                            Back
                        </button>
                        <button
                            type="button"
                            onClick={handleFinalSubmit}
                            disabled={pickupPhotos.length === 0 || isSubmitting}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isSubmitting ? 'Submitting...' : 'Request Pickup'}
                        </button>
                    </div>
                }
            >
                <PickupLabelsStep
                    boxes={generatedBoxes}
                    mergedPdfUrl={mergedPdfUrl}
                    noPrinter={noPrinter}
                    onNoPrinterChange={setNoPrinter}
                    photos={pickupPhotos}
                    onPhotosChange={setPickupPhotos}
                    pickupType={selectedType}
                />
                <ConfirmationModal
                    isOpen={showBackWarning}
                    onClose={() => setShowBackWarning(false)}
                    onConfirm={confirmBack}
                    title="Invalidate Generated Labels?"
                    message={
                        <div className="space-y-2 text-sm text-gray-700">
                            <p>
                                Going back and re-continuing will generate new box codes and labels.
                            </p>
                            <p>
                                Any labels you have already printed or written on your packages will
                                no longer be valid.
                            </p>
                        </div>
                    }
                    confirmText="Yes, Go Back"
                    cancelText="Stay"
                    type="warning"
                />
            </ModalShell>
        );
    }

    return null;
};

export default PickupForm;